'use client';

import { useTranslations } from 'next-intl';
import { type FC, type MouseEvent, type ReactNode, useMemo } from 'react';

import clsx from 'clsx';

import { MenuItem as MenuItemComponent } from '@/app/[locale]/(authorized)/components/Menu/components/MenuItem';
import { Flex } from '@/components/common/Flex';
import { CascadeGateIcon } from '@/components/common/icons/CascadeGateIcon';
import { ClipboardAltIcon } from '@/components/common/icons/ClipboardAltIcon';
import { CommentIcon } from '@/components/common/icons/CommentIcon';
import { CreditCardIcon } from '@/components/common/icons/CreditCardIcon';
import { EnterIcon } from '@/components/common/icons/EnterIcon';
import { ExitIcon } from '@/components/common/icons/ExitIcon';
import { LayoutIcon } from '@/components/common/icons/LayoutIcon';
import { MobilePhoneIcon } from '@/components/common/icons/MobilePhoneIcon';
import { ProfileIcon } from '@/components/common/icons/ProfileIcon';
import { SettingsAltIcon } from '@/components/common/icons/SettingsAltIcon';
import { Link } from '@/components/common/Link';
import { Logo } from '@/components/common/Logo';
import { LogoText } from '@/components/common/LogoText';
import { OverflowingTextTip } from '@/components/common/OverflowingTextTip';
import { Spinner } from '@/components/common/Spinner';
import { urls } from '@/constants/urls';
import { useAuth } from '@/hooks/auth/use-auth';
import { useFullUserInfo } from '@/hooks/auth/use-full-user-info';
import { type WithClassName } from '@/types/react';

import styles from './Menu.module.scss';

export type MenuItem = {
  link: string;
  sectionLink?: string;
  isExact?: boolean;
  isActive?: (pathname: string) => boolean;
  onboardingTarget?: string;
  icon: ReactNode;
  content: ReactNode;
  onClick?: (e: MouseEvent) => unknown;
};

type MenuProps = WithClassName & {
  mobileCloseButton?: ReactNode;
};

export const Menu: FC<MenuProps> = (props) => {
  const t = useTranslations('menu');

  const { className, mobileCloseButton } = props;

  const { logout } = useAuth();
  const { data: fullUserInfo, loading: loadingFullUserInfo } = useFullUserInfo();

  const topMenu = useMemo(
    (): MenuItem[] => [
      {
        link: urls.Dashboard,
        isExact: true,
        onboardingTarget: 'dashboard',
        icon: <LayoutIcon />,
        content: t('links.dashboard'),
      },
      {
        link: urls.PaymentGates,
        onboardingTarget: 'gates',
        icon: <MobilePhoneIcon />,
        content: t('links.gates'),
      },
      {
        link: urls.InvoicesTransactions,
        sectionLink: urls.Invoices,
        onboardingTarget: 'invoices',
        icon: <EnterIcon />,
        content: t('links.invoices'),
      },
      {
        link: urls.Cascades,
        sectionLink: urls.Cascades,
        onboardingTarget: 'cascades',
        icon: <CascadeGateIcon />,
        content: t('links.cascades'),
      },
      {
        link: urls.WithdrawalsTransactions,
        sectionLink: urls.Withdrawals,
        onboardingTarget: 'withdrawals',
        icon: <CreditCardIcon />,
        content: t('links.withdrawals'),
      },
      {
        link: urls.ReportsAll,
        sectionLink: urls.Reports,
        onboardingTarget: 'reports',
        icon: <ClipboardAltIcon />,
        content: t('links.reports'),
      },
    ],
    [t]
  );

  const bottomMenu = useMemo(
    (): MenuItem[] => [
      // {
      //   link: urls.Help,
      //   icon: <CommentIcon />,
      //   content: t('links.help'),
      // },
      {
        link: urls.Settings,
        onboardingTarget: 'settings',
        icon: <SettingsAltIcon />,
        content: t('links.settings'),
      },
      {
        link: urls.Login,
        icon: <ExitIcon />,
        content: t('links.logout'),
        onClick: (e) => {
          e.preventDefault();

          logout();
        },
      },
    ],
    [logout, t]
  );

  return (
    <Flex
      className={clsx(styles.root, className)}
      direction="column"
      gap={16}
      justifyContent="spaceBetween"
      childMinSize="unset"
    >
      <Flex direction="column">
        <Flex className={styles.header}>
          {mobileCloseButton}

          <Link className={styles.logoContainer} href={urls.Dashboard}>
            <LogoText />

            <Logo />
          </Link>
        </Flex>

        <Flex direction="column">
          {topMenu.map((item) => (
            <MenuItemComponent key={item.link} item={item} />
          ))}
        </Flex>
      </Flex>

      <Flex className={styles.bottomBlock} direction="column" gap={16}>
        <Flex direction="column">
          {bottomMenu.map((item) => (
            <MenuItemComponent key={item.link} item={item} />
          ))}
        </Flex>
      </Flex>

      <Flex className={styles.profileInfo} gap={4} alignItems="center">
        <ProfileIcon />

        {loadingFullUserInfo ? (
          <Spinner color="white-50p" />
        ) : (
          <Flex direction="column" gap={1}>
            {fullUserInfo?.userInfo?.name && (
              <OverflowingTextTip type="regular16">{fullUserInfo.userInfo.name}</OverflowingTextTip>
            )}

            <OverflowingTextTip type="regular14" color="white-50p">
              {t('info.merchantId', {
                id: fullUserInfo?.merchantInfo?.id ?? 0,
              })}
            </OverflowingTextTip>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
